import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import type { Perm, Role } from '@ouradent/shared';
import { supabase } from '../lib/supabase';

export type Me = {
  id: string;
  fullName: string;
  email: string;
  role: Role;
  external: boolean;
  perms: Perm[];
};

type SessionState = {
  loading: boolean;
  session: Session | null;
  me: Me | null;
  can: (perm: Perm) => boolean;
  signOut: () => Promise<void>;
  needsPassword: boolean;
  passwordSet: () => void;
};

// An invite or reset link lands with its type in the URL hash; supabase-js
// clears it, so it is read once, before the client starts.
const LINK_NEEDS_PASSWORD = /type=(invite|recovery)/.test(window.location.hash);

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [needsPassword, setNeedsPassword] = useState(LINK_NEEDS_PASSWORD);

  useEffect(() => {
    let active = true;

    supabase.auth.getSession().then(({ data }) => {
      if (active) setSession(data.session);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      if (event === 'PASSWORD_RECOVERY') setNeedsPassword(true);
      setSession(next);
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!session) {
      setMe(null);
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);

    // Both reads go through RLS: you can always see your own profile and your
    // own permissions, and nothing else here.
    void (async () => {
      const [{ data: profile }, { data: perms }] = await Promise.all([
        supabase
          .from('profiles')
          .select('id, full_name, email, role, external')
          .eq('id', session.user.id)
          .single(),
        supabase.from('permissions').select('perm').eq('user_id', session.user.id),
      ]);

      if (!active) return;

      setMe(
        profile
          ? {
              id: profile.id as string,
              fullName: profile.full_name as string,
              email: profile.email as string,
              role: profile.role as Role,
              external: profile.external as boolean,
              perms: (perms ?? []).map((p) => p.perm as Perm),
            }
          : null,
      );
      setLoading(false);
    })();

    return () => {
      active = false;
    };
  }, [session]);

  const value = useMemo<SessionState>(
    () => ({
      loading,
      session,
      me,
      can: (perm) => me?.perms.includes(perm) ?? false,
      signOut: async () => {
        await supabase.auth.signOut();
      },
      needsPassword,
      passwordSet: () => setNeedsPassword(false),
    }),
    [loading, session, me, needsPassword],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside SessionProvider');
  return ctx;
}
